/**
 * Heads-Up NLHE Poker の TypeScript 表現 (mixed-engine serde 形と一致)。
 *
 * MVP では mental poker (commit-reveal) 暗号は engine 側 stub で、 デックは engine
 * が直接 RNG から派生する。 frontend は state.current_hand.hole / state.current_hand.board
 * を表示するだけ。 後段で commit-reveal 完成時は、 hole を private にする経路を追加する。
 */

export type Side = 'First' | 'Second';

export interface Card {
  rank: number; // 2..=14 (Ace = 14)
  suit: number; // 0..=3
}

export type Phase =
  | 'preflop'
  | 'flop'
  | 'turn'
  | 'river'
  | 'all_in_run_out'
  | 'showdown'
  | 'hand_complete'
  | 'match_complete';

export type Seat = 'sb' | 'bb';

export type PokerMove =
  | 'fold'
  | 'check'
  | 'call'
  | { bet: { amount: number } }
  | { raise: { to_total: number } }
  | 'all_in'
  | 'next_hand'
  /** mental poker (= docs §8.10、 B3-A 以降): 各 client が **自分の seat の hole** を
   *  engine に提出。 to_act の seat と一致が必要、 SB → BB の順で 1 回ずつ apply される。 */
  | { provide_my_hole: { seat: Seat; cards: [Card, Card] } }
  /** mental poker: flop / turn / river / runout の board cards を engine に提出。 */
  | { provide_board_cards: { cards: Card[] } };

/** engine の AwaitingCards と一致。 `awaiting_cards = Some(...)` の時 ベッティング前に
 *  対応する provide が必要 (= legal は provide テンプレ 1 つだけが返る)。 */
export type AwaitingCards = 'holes' | 'flop' | 'turn' | 'river' | 'all_in_run_out';

export interface BlindLevel {
  sb: number;
  bb: number;
  bb_ante: number;
}

export interface BlindSchedule {
  hands_per_level: number;
  levels: BlindLevel[];
}

export interface HandState {
  hand_number: number;
  dealer: Seat;
  phase: Phase;
  /** [Sb, Bb] stacks */
  stacks: [number, number];
  pot: number;
  current_round_bet: [number, number];
  min_raise_step: number;
  last_aggressor: Seat | null;
  to_act: Seat;
  blinds: [number, number]; // (SB, BB)
  bb_ante: number;
  acted_this_round: [boolean, boolean];
  /** [Sb hole, Bb hole] — mental poker protocol で FE が provide するまで null */
  hole: ([Card, Card] | null)[];
  board: Card[];
  /** 「いま FE からカード提供を待っている」 状態。 null なら通常進行。 */
  awaiting_cards: AwaitingCards | null;
  winner: Seat[] | null;
  pot_split: [number, number] | null;
  showdown_score: [number, number] | null;
}

export interface PokerOutcome {
  decisive?: { winner: Side; hands_played: number };
}

/** 直前ハンドのサマリ (= engine `CompletedHandSummary` と一致)。
 *  FE 履歴ペインはこれを source of truth として表示する。
 *  `dealer_side_at_time` は **ハンド時点の SB Side**。 `state.current_dealer_side` は
 *  rotate 後の値になるので、 履歴判定は必ずこちらを使う。 */
export interface CompletedHandSummary {
  hand_number: number;
  winner: Seat[];
  pot: number;
  board: Card[];
  hole: ([Card, Card] | null)[];
  dealer_side_at_time: Side;
}

export interface PokerMatchState {
  starting_stack: number;
  blind_schedule: BlindSchedule;
  /** [Leader, Follower] (= [First, Second]) */
  stacks: [number, number];
  current_hand: HandState;
  current_dealer_side: Side;
  initial_dealer_side: Side;
  outcome: PokerOutcome | null;
  completed_hands: number;
  last_completed_hand: CompletedHandSummary | null;
  deck_seed: number;
}

export function rankLabel(r: number): string {
  if (r === 14) return 'A';
  if (r === 13) return 'K';
  if (r === 12) return 'Q';
  if (r === 11) return 'J';
  if (r === 10) return 'T';
  return r.toString();
}

export function suitLabel(s: number): string {
  // 0=spades, 1=hearts, 2=diamonds, 3=clubs (engine convention)
  return ['♠', '♥', '♦', '♣'][s] ?? '?';
}

export function suitClass(s: number): string {
  return ['s', 'h', 'd', 'c'][s] ?? 'x';
}

export function isBet(m: PokerMove): m is { bet: { amount: number } } {
  return typeof m === 'object' && 'bet' in m;
}
export function isRaise(m: PokerMove): m is { raise: { to_total: number } } {
  return typeof m === 'object' && 'raise' in m;
}
